<template>
  <div>
    <Loading
      v-if="isLoading"
      loading-message="載入中..."
      :background-color="'#ffffff'"
    />

    <main>
      <div class="px-3">
        <h2 class="section-title mt-4">預選清單</h2>
        <p class="total-quantity">您的預選單共計<span class="total-quantity--emphasis">{{ totalQuantity }}</span>項商品</p>
        <div class="products">
          <div
            class="products__item"
            v-for="sku in formattedSkusInCart"
            :key="sku.id"
          >
            <img
              :src="sku.image"
              class="products__item__photo"
            />
            <div class="products__item__title">
              {{ sku.title }}
            </div>
            <div class="products__item__price" v-if="priceIsVisible">
              <template v-if="sku.is_discounted">
                <span class="promoting">${{ applyThousandsSeperator(sku.discounted_price) }}</span>
                <span class="original-price ml-2">$ {{ applyThousandsSeperator(sku.original_price) }}</span>
              </template>
              <div v-else>${{ applyThousandsSeperator(sku.original_price) }}</div>
            </div>
            <div class="products__item__price" v-if="pointIsVisible">
              <span class="price">{{ sku.point }} 點</span>
            </div>
            <div class="products__item__description" v-html="sku.description"></div>
            <div class="products__item__quantity">
              <b-form-spinbutton
                size="sm"
                inline
                :value="cart[sku.id]"
                @change="(quantity) => handleSkuQuantityChange(sku.id, quantity)"
                min="1"
                :class="{ 'increment-disabled': cannotAddMoreQuantity(sku) }"
              ></b-form-spinbutton>
            </div>
          </div>
        </div>

        <div class="divider mt-4 mb-1"></div>
        <div class="total-price" v-if="priceIsVisible">
          TOTAL
          <span class="total-price--emphasis ml-3 mr-2">
            {{ applyThousandsSeperator(totalPrice) }}
          </span>
          元
        </div>
        <div class="total-price" v-if="pointIsVisible">
          TOTAL
          <span class="total-price--emphasis ml-3 mr-2">
            {{ applyThousandsSeperator(totalPoints) }}
          </span>
          點
        </div>

        <div class="preorder-cart__terms mt-4" v-if="terms !== null" v-html="terms"></div>
      </div>

      <img class="w-100 mt-4" v-if="topImageUrl" :src="topImageUrl">

      <div class="px-3">
        <h2 class="shop-name mt-4" v-if="branchId">{{ deepGet(branch, 'name') }}</h2>
        <h2 class="section-title mt-4">個人資料</h2>
        <div class="dynamic-form mt-4">
          <DynamicForm ref="checkoutForm" :input-subjects="fields" v-model="inputForm" />
        </div>
      </div>

      <img class="w-100 mt-4" v-if="bottomImageUrl" :src="bottomImageUrl">
    </main>

    <footer class="sticky-bottom footer-action-bar">
      <ShareButton
        @click="$router.push({ name: 'LiffPreorderProducts', query: { branch_id: branchId } })"
        class="footer-action-bar__checkout-button"
        variant="s-btn-outline-primary bg-transparent"
      >
        返回前頁
      </ShareButton>
      <ShareButton
        @click="handleSubmitButtonClicked"
        class="footer-action-bar__checkout-button"
        variant="primary"
        :disabled="totalQuantity === 0 || isPointMode && totalPoints > memberPoints"
      >
        送出訂單
      </ShareButton>
    </footer>
  </div>
</template>

<script>
import branchApi from "@/apis/liff/v2/branch";
import preorderApi from "@/apis/liff/v2/preorder";
import DynamicForm from "@/components/DynamicForm/DynamicForm";
import ShareButton from "@/components/Page/Liff/Shared/Button";
import Loading from "@/components/Page/Liff/Shared/Loading";
import themeColor from "@/mixins/liff/themeColor";
import PreorderSetting from "@/models/PreorderSetting.model"
import Sku from "@/models/Sku.model"
import { applyThousandsSeperator } from "@/utils/numberUtils";
import _ from "lodash"
import deepGet from "lodash/get"
import { mapActions, mapGetters, mapState } from "vuex";

export default {
  mixins: [themeColor],
  components: {
    DynamicForm,
    ShareButton,
    Loading,
  },
  data() {
    return {
      inputForm: {},
      isLoading: false,
      branch: null,
    };
  },
  computed: {
    ...mapGetters('liffPreorder', ['totalQuantity']),
    ...mapState('liffPreorder', { preorderSetting: 'configuration', cart: 'cart', cachedSkus: 'cachedSkus', memberPoints: 'memberPoints' }),
    preorderSettingId() {
      return this.$route.params.preorderSettingId
    },
    branchId() {
      return this.$route.query.branch_id
    },
    preorderSettingDataModel() {
      return new PreorderSetting(this.preorderSetting)
    },
    formattedSkusInCart() {
      return Object.values(this.cachedSkus).filter(sku => Object.keys(this.cart).includes(sku.id))
      .map(sku => {
        const skuModel = new Sku(sku)
        return {
          id: skuModel.id,
          title: skuModel.nameWithProductName,
          description: (skuModel.description || '').replace(/\n/g, "<br>"),
          original_price: skuModel.originalPrice,
          discounted_price: skuModel.discountedPrice,
          is_discounted: skuModel.isDiscounted,
          point: skuModel.point,
          image: skuModel.image,
          remaining_stock: skuModel.remainingStock,
        }
      })
    },
    fields() {
      return deepGet(this.preorderSetting, 'config.fields', []).map((field) => {
        return {
          id: deepGet(field, '_id') ?? deepGet(field, 'config._id'),
          ...field,
          ...field.config,
        };
      })
    },
    priceIsVisible() {
      return this.preorderSettingDataModel.isMoneyMode
    },
    pointIsVisible() {
      return this.preorderSettingDataModel.isPointMode
    },
    isPointMode() {
      return this.preorderSettingDataModel.isPointMode
    },
    totalPrice() {
      return _.sum(this.formattedSkusInCart.map(sku => sku.discounted_price * this.cart[sku.id]))
    },
    totalPoints() {
      return _.sum(this.formattedSkusInCart.map(sku => sku.point * this.cart[sku.id]))
    },
    topImageUrl() {
      return deepGet(this.preorderSetting, 'config.confirm_page.top_image_url')
    },
    bottomImageUrl() {
      return deepGet(this.preorderSetting, 'config.confirm_page.bottom_image_url')
    },
    terms() {
      return deepGet(this.preorderSetting, 'config.confirm_page.terms', null)
    },
  },
  async mounted() {
    if (Object.keys(this.cart).length === 0) {
      return this.$router.replace({ name: 'LiffPreorderProducts', query: { branch_id: this.branchId } })
    }

    if (this.branchId) {
      this.branch = _.get(await branchApi.getSingleBranch(this.branchId), 'data.data')
    }
  },
  methods: {
    applyThousandsSeperator,
    deepGet: _.get,
    ...mapActions('liffPreorder', ['clearCart', 'updateCartQuantity']),
    handleSkuQuantityChange(skuId, quantity) {
      this.updateCartQuantity({ skuId, quantity })
    },
    async handleSubmitButtonClicked() {
      const validationPasses = await this.$refs.checkoutForm.validate()
      if (false === validationPasses) {
        return
      }

      const submissionIsConfirmed = await this.$swal({
        text: '確定要送出訂單嗎？',
        reverseButtons: true,
        showCancelButton: true,
        confirmButtonText: '確定',
        cancelButtonText: '取消',
      })
      if (deepGet(submissionIsConfirmed, 'value', false) === false) {
        return
      }

      this.isLoading = true
      try {
        await preorderApi.create(
          this.preorderSettingId,
          {
            fields: this.inputForm,
            items: Object.keys(this.cart).map(skuId => ({ sku_id: skuId, quantity: this.cart[skuId]})),
            branch_id: this.branchId,
          },
        )
        this.clearCart()
        this.$router.push({ name: 'LiffPreorderFinished' })
      } catch (e) {
        let message = deepGet(e, 'response.data.message', '送出失敗')
        if (typeof message === 'object') {
          message = Object.values(message).join(' ')
        }
        this.$swal.fire({ title: '送出失敗', text: message, type: "error" })
        this.isLoading = false
      }
    },
    cannotAddMoreQuantity(sku) {
      return (this.isPointMode && (this.totalPoints + sku.point) > this.memberPoints) ||
        (this.preorderSettingDataModel.productStockIsEnabled && sku.remaining_stock <= this.cart[sku.id])
    },
  },
};
</script>

<style lang="scss" scoped>
.products {
  &__item + &__item {
    margin-top: 16px;
  }

  &__item {
    color: #212121;
    background-color: white;
    text-decoration: none !important;
    display: grid;
    grid-template-columns: auto 1fr;
    grid-template-rows: auto auto auto 1fr;
    grid-template-areas:
      "photo title"
      "photo price"
      "photo description"
      "photo quantity";

    &__photo {
      grid-area: photo;
      width: clamp(120px, 33vw, 240px);
      object-fit: cover;
      aspect-ratio: 1 / 1.2;

      @media screen and (min-width: 512px) {
        width: clamp(120px, 25vw, 240px);
      }
    }

    &__title {
      grid-area: title;
      display: -webkit-box;
      margin-top: 16px;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &__price {
      grid-area: price;
      margin-top: 8px;
      font-size: 0.875rem;
      color: #aaa;

      .promoting {
        font-size: 1rem;
        font-weight: normal;
        color: var(--primary-color);
      }

      .original-price {
        text-decoration: line-through;
        color: #aaa;
      }
    }

    &__description {
      grid-area: description;
      display: -webkit-box;
      margin-top: 8px;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
      font-size: 0.875rem;
      color: #888;
    }

    &__quantity {
      grid-area: quantity;
      display: flex;
      margin-top: 8px;
      margin-bottom: 16px;
      align-items: flex-end;

      ::v-deep .b-form-spinbutton {
        height: 36px;
        padding: 0;

        output {
          text-align: center !important;
        }
      }
    }

    &__title, &__price, &__description, &__quantity {
      padding: 0 16px;
    }
  }
}

.shop-name {
  padding: 16px;
  background-color: #f8f8f8;
  font-size: 1.1rem;
  font-weight: 500;
  color: #444;
  text-align: center;
}

.section-title {
  font-size: 1.1rem;
  font-weight: 500;
  color: #444;
  text-align: center;
}

.total-quantity {
  color: #777;
  text-align: center;

  &--emphasis {
    margin: 0 8px;
    font-size: 1.25rem;
    font-weight: 500;
    color: #444;
  }
}

.total-price {
  font-size: .875rem;
  color: #444;
  text-align: right;

  &--emphasis {
    font-size: 1.5rem;
  }
}

.preorder-cart__terms {
  padding: 16px;
  background-color: #f8f8f8;
}

.divider {
  height: 1px;
  width: 100%;
  background-color: #ccc;
}

.footer-action-bar {
  max-width: 768px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background-color: white;
  box-shadow: 0px -2px 4px 0px #0000000D;

  &__checkout-button {
    flex: 0 1 50%;
    padding: 12px;
  }

  &__checkout-button + &__checkout-button {
    margin-left: 16px;
  }
}

.sticky-bottom {
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
}


::v-deep .form-check-input{
  margin:3px 3px 0px 4px;
}

::v-deep .form-check{
  margin-top: 0;
}

::v-deep .increment-disabled [aria-label="Increment"] {
  opacity: 0.3;
  pointer-events: none;
}
</style>
